import React, { useState, useEffect, useCallback } from 'react'
import { useRecordContext, useNotify } from 'react-admin'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TablePagination,
  Autocomplete,
  TextField,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SendIcon from '@mui/icons-material/Send'
import EditIcon from '@mui/icons-material/Edit'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { API_URL } from '../../services/settings'
import { campaignLeadStatusColors, sendMethodColors } from './constants'

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString()
}

const CampaignLeads = () => {
  const record = useRecordContext()
  const notify = useNotify()
  const [leads, setLeads] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [selectedLeadId, setSelectedLeadId] = useState(null)
  const [selectedLead, setSelectedLead] = useState(null)
  const [searchLeads, setSearchLeads] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sendingLeadId, setSendingLeadId] = useState(null)
  const [methodAnchorEl, setMethodAnchorEl] = useState(null)
  const [editingLeadId, setEditingLeadId] = useState(null)

  const fetchLeads = useCallback(async () => {
    if (!record) return
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(
        `${API_URL}/campaigns/${record.id}/leads?page=${page + 1}&page_size=${rowsPerPage}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      )
      if (response.ok) {
        const data = await response.json()
        setLeads(data.results || [])
        setTotal(data.count || 0)
      }
    } catch (e) {
      // Silently fail
    }
  }, [record, page, rowsPerPage])

  useEffect(() => {
    fetchLeads()
  }, [fetchLeads])

  const handleSearchLeads = async (q) => {
    if (!q || q.length < 2) {
      setSearchLeads([])
      return
    }
    setSearchLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/leads?q=${encodeURIComponent(q)}&page_size=10`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setSearchLeads(data.results || [])
      }
    } catch (e) {
      // silently fail
    } finally {
      setSearchLoading(false)
    }
  }

  const handleAddLead = async () => {
    if (!selectedLeadId || !record) return
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/campaigns/${record.id}/leads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ lead_id: selectedLeadId }),
      })
      const data = await response.json()
      if (response.ok) {
        notify('Lead added to campaign', { type: 'success' })
        setAddDialogOpen(false)
        setSelectedLeadId(null)
        setSelectedLead(null)
        setSearchLeads([])
        fetchLeads()
      } else {
        notify(data.message || 'Failed to add lead', { type: 'error' })
      }
    } catch (e) {
      notify('Error: ' + e.message, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveLead = async (leadId) => {
    if (!record) return
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/campaigns/${record.id}/leads/${leadId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        notify('Lead removed from campaign', { type: 'success' })
        fetchLeads()
      } else {
        const data = await response.json()
        notify(data.message || 'Failed to remove lead', { type: 'error' })
      }
    } catch (e) {
      notify('Error: ' + e.message, { type: 'error' })
    }
  }

  const handleSendLead = async (leadId, isFollowup = false) => {
    if (!record) return
    setSendingLeadId(leadId)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(
        `${API_URL}/campaigns/${record.id}/leads/${leadId}/send?is_followup=${isFollowup}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        }
      )
      const data = await response.json()
      if (response.ok) {
        notify(data.message || 'Email sent successfully', { type: 'success' })
        fetchLeads()
      } else {
        notify(data.message || 'Failed to send email', { type: 'error' })
      }
    } catch (e) {
      notify('Error: ' + e.message, { type: 'error' })
    } finally {
      setSendingLeadId(null)
    }
  }

  const handleOpenMethodMenu = (e, leadId) => {
    setMethodAnchorEl(e.currentTarget)
    setEditingLeadId(leadId)
  }

  const handleCloseMethodMenu = () => {
    setMethodAnchorEl(null)
    setEditingLeadId(null)
  }

  const handleChangeSendMethod = async (method) => {
    if (!record || !editingLeadId) return
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(
        `${API_URL}/campaigns/${record.id}/leads/${editingLeadId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ send_method: method }),
        }
      )
      const data = await response.json()
      if (response.ok) {
        notify(`Send method changed to ${method}`, { type: 'success' })
        fetchLeads()
      } else {
        notify(data.message || 'Failed to update send method', { type: 'error' })
      }
    } catch (e) {
      notify('Error: ' + e.message, { type: 'error' })
    } finally {
      handleCloseMethodMenu()
    }
  }

  if (!record) return null

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          size="small"
        >
          Add Lead
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Send Method</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sender</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell>Opened</TableCell>
              <TableCell>Follow-up Sent</TableCell>
              <TableCell>Follow-up Opened</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leads.length === 0 && (
              <TableRow>
                <TableCell colSpan={11}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                    No leads in this campaign
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {leads.map((cl) => (
              <TableRow key={cl.id}>
                <TableCell>{cl.lead?.full_name || '-'}</TableCell>
                <TableCell>{cl.lead?.email || '-'}</TableCell>
                <TableCell>{cl.lead?.company || '-'}</TableCell>
                <TableCell>
                  {cl.send_method && (
                    <Chip
                      icon={<EditIcon />}
                      label={cl.send_method}
                      color={sendMethodColors[cl.send_method] || 'default'}
                      size="small"
                      variant="filled"
                      onClick={(e) => handleOpenMethodMenu(e, cl.lead?.id || cl.lead_id)}
                      sx={{ cursor: 'pointer', fontWeight: 600 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={cl.message_sent || cl.followup_message_sent || 'No message saved'} arrow max-width="400px">
                    <Chip
                      label={cl.status}
                      color={campaignLeadStatusColors[cl.status] || 'default'}
                      size="small"
                      sx={{ cursor: cl.message_sent || cl.followup_message_sent ? 'help' : 'default' }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>{cl.assigned_sender?.fullname || '-'}</TableCell>
                <TableCell>{formatDate(cl.sent_at)}</TableCell>
                <TableCell>{formatDate(cl.opened_at)}</TableCell>
                <TableCell>{formatDate(cl.followup_sent_at)}</TableCell>
                <TableCell>{formatDate(cl.followup_opened_at)}</TableCell>
                <TableCell align="right">
                  {cl.status === 'pending' && (
                    <IconButton
                      size="small"
                      onClick={() => handleSendLead(cl.lead?.id || cl.lead_id, false)}
                      disabled={sendingLeadId === (cl.lead?.id || cl.lead_id)}
                      title="Send email"
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  )}
                  {cl.status === 'sent' && !cl.followup_sent_at && (
                    <IconButton
                      size="small"
                      onClick={() => handleSendLead(cl.lead?.id || cl.lead_id, true)}
                      disabled={sendingLeadId === (cl.lead?.id || cl.lead_id)}
                      title="Send follow-up"
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveLead(cl.lead?.id || cl.lead_id)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>

      {/* Send Method Menu */}
      <Menu
        anchorEl={methodAnchorEl}
        open={Boolean(methodAnchorEl)}
        onClose={handleCloseMethodMenu}
      >
        <MenuItem onClick={() => handleChangeSendMethod('kernel')}>
          Kernel (Flask-Mail)
        </MenuItem>
        <MenuItem onClick={() => handleChangeSendMethod('lemlist')}>
          Lemlist
        </MenuItem>
        <MenuItem onClick={() => handleChangeSendMethod('auto')}>
          Auto (by sender)
        </MenuItem>
      </Menu>

      {/* Add Lead Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Lead to Campaign</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={searchLeads}
              getOptionLabel={(option) => option.display_name || ''}
              value={selectedLead}
              onChange={(e, newValue) => {
                setSelectedLead(newValue)
                setSelectedLeadId(newValue?.id || null)
              }}
              onInputChange={(e, value, reason) => {
                if (reason === 'input') {
                  handleSearchLeads(value)
                }
              }}
              loading={searchLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Lead"
                  placeholder="Type lead name or email..."
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddLead}
            disabled={!selectedLeadId || loading}
          >
            {loading ? 'Adding...' : 'Add Lead'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CampaignLeads
